import { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api, streamJob } from '../../lib/api.js'
import { Spinner, Empty, relTime } from '../../components/ui.jsx'

const ETIQUETA = { ok: 'correcta', error: 'con error', running: 'en curso', queued: 'en cola' }
const CLASE = { ok: 'done', error: 'error', running: 'running', queued: 'running' }

/** Detalle de una ejecución. Si sigue viva se engancha al SSE; si no, muestra el log guardado. */
function Detalle({ slug, id }) {
  const job = useQuery({ queryKey: ['job', slug, id], queryFn: () => api.job(slug, id), retry: false })
  const [lineas, setLineas] = useState(null)
  const cerrar = useRef(null)

  useEffect(() => {
    if (!job.data || job.data.completed) return
    setLineas([])
    cerrar.current = streamJob(slug, id, {
      onLine: (l) => setLineas((p) => [...(p ?? []), l]),
      onEnd: () => job.refetch(),
    })
    return () => cerrar.current?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.data?.completed, id])

  if (job.isPending) return <Spinner />
  if (job.isError) return <div className="alert error">{job.error.body?.detail ?? job.error.message}</div>

  const d = job.data
  const texto = lineas ?? String(d.logs ?? '').split('\n')

  return (
    <>
      <div className="section-head">
        <div>
          <h1 className="h1" style={{ fontFamily: 'var(--mono)', fontSize: 18 }}>{d.path?.replace(`f/${slug}/`, '')}</h1>
          <p className="lede">
            <Link to={`/p/${slug}/runs`}>Ejecuciones</Link> · {d.kind} · lanzada por {d.by}
            {d.durationMs != null && ` · ${d.durationMs} ms`}
          </p>
        </div>
        {d.path && (
          <Link className="btn secondary" to={`/p/${slug}/functions/edit?path=${encodeURIComponent(d.path)}`}>
            Abrir en el editor
          </Link>
        )}
      </div>

      <div className="logs">
        {texto.filter((l) => l !== '').map((l, i) => <div className="l" key={i}>{l}</div>)}
        {texto.every((l) => l === '') && <div className="l" style={{ color: 'var(--fg-3)' }}>sin salida</div>}
      </div>

      {d.completed && d.result !== null && (
        <>
          <div className="field-label" style={{ marginTop: 12 }}>Resultado</div>
          <div className="logs"><div className="l">{JSON.stringify(d.result, null, 2)}</div></div>
        </>
      )}
    </>
  )
}

export function Runs() {
  const { slug, id } = useParams()
  const lista = useQuery({
    queryKey: ['jobs', slug],
    queryFn: () => api.jobs(slug, 50),
    // Mientras haya algo corriendo, refresca solo: la lista es la vista de guardia.
    refetchInterval: (q) => (q.state.data?.some((j) => j.state === 'running' || j.state === 'queued') ? 3000 : false),
    enabled: !id,
  })

  if (id) return <Detalle slug={slug} id={id} />

  return (
    <>
      <h1 className="h1">Ejecuciones</h1>
      <p className="lede">Últimas ejecuciones de las funciones del proyecto, incluidas las pruebas del editor.</p>

      {lista.isPending && <Spinner />}
      {lista.isError && (
        <div className="alert error">
          No se pudo hablar con Windmill · <code>{lista.error.body?.detail ?? lista.error.message}</code>
        </div>
      )}
      {lista.data?.length === 0 && (
        <Empty title="Sin ejecuciones todavía">Aparecerán aquí en cuanto pruebes o lances una función.</Empty>
      )}

      {lista.data?.length > 0 && (
        <div className="card"><div className="table-scroll">
          <table className="t">
            <thead><tr><th>Estado</th><th>Función</th><th>Tipo</th><th>Quién</th><th>Cuándo</th><th>Duración</th></tr></thead>
            <tbody>
              {lista.data.map((j) => (
                <tr key={j.id}>
                  <td>
                    <Link to={`/p/${slug}/runs/${j.id}`}>
                      <span className={`badge ${CLASE[j.state] ?? ''}`}><span className="dot" />{ETIQUETA[j.state] ?? j.state}</span>
                    </Link>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)' }}>{j.path?.replace(`f/${slug}/`, '')}</td>
                  <td>{j.kind === 'preview' ? 'prueba' : 'guardada'}</td>
                  <td className="ellipsis">{j.by}</td>
                  <td>{relTime(j.createdAt)}</td>
                  <td>{j.durationMs != null ? `${j.durationMs} ms` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}
    </>
  )
}
